// PackageService — the package / permission-derivation / limit-signal logic, rescued from two
// legacy homes into one PURE, Vue-free, store-free service (contract 07 §8):
//   • `AccountService.computePermissionsAndPackage` (v3 `service/AccountService.ts:63-80`) — the
//     workspace-scoped permission OVERRIDE + package resolution.
//   • `applyUserPackages` from the dying v1 flow (v3 `flows/AccountBootstrapFlow.ts:87-138`) — the
//     pricing / limit / onboarding signals. The magic numbers there had no name; they are named
//     here as package-tier config. The service returns a SIGNAL only — showing a modal is a
//     credits/account UI concern and the app stays `ready` (degraded, not blocked).
// No store/HTTP/Vue import: every input is passed in, so the whole surface is unit-testable and
// the flow (a later task) injects the live store/workspace values.
import { normalizePackageId } from '../models/account.types'
import type { AccountDetail, PermissionMap, PermissionValue } from '../models/account.types'

// ── Package-tier constants (contract §8 acceptance: name the magic numbers) ──
/** The paid "creative" package. Absence-of-package only forces pricing when NOT on this tier
 *  (legacy `accountDetail.package !== 12`), and it is the `isCreativePkg` tier (R10). */
export const CREATIVE_PACKAGE_ID = 12
/** The tier that is always shown the pricing modal (legacy `packageId === 7`). */
export const PRICING_LOCKED_PACKAGE_ID = 7

// ── Permission keys that carry package limits (legacy `getPermission('…')`) ──
const WORKSPACE_LIMIT_KEY = 'workspaces_'
const USER_LIMIT_KEY = 'users_'
const DASHBOARD_LIMIT_KEY = 'custom_dashboard_'

/** The one signal the flow acts on after a boot/switch: which credits/account modal (if any) to
 *  raise, or that onboarding is due. `none` = nothing to surface. */
export type PackageSignal =
  | { kind: 'none' }
  | { kind: 'pricing' }
  | { kind: 'limit'; reason: 'workspaces' | 'users' | 'dashboards' }
  | { kind: 'onboarding' }

export interface PackageSignalInput {
  /** The resolved package id (from `computePermissionsAndPackage`). */
  packageId: number | null
  /** The account's raw package field (legacy compared `!== 12` before normalization). */
  accountPackage: number | number[] | null | undefined
  /** The effective permissions (workspace-override applied), for the numeric limits. */
  permissions: PermissionMap
  workspaceCount: number
  connectedUserCount: number
  dashboardCount: number
}

const toLimit = (value: PermissionValue): number => Number(value) || 0

export class PackageService {
  /**
   * Workspace-scoped permissions OVERRIDE the account-level map, and the package id is resolved
   * from the workspace package first, then the account package (both via `normalizePackageId`).
   * Pure — no store, no HTTP. Ported from v3 `AccountService.computePermissionsAndPackage`.
   *
   * Account-level precedence (BE-ACC-01): `rbacPermissions` — the authoritative `rbac.permissions`
   * value-map — outranks `detail.permissions`, which stays as the fallback for a boot whose `rbac`
   * section failed. Both are server-sent maps from the same producer (`get_users_permissions`), so
   * neither is a client-side grant. The workspace override still wins over both: `rbac.permissions`
   * is always ACCOUNT-scoped (the section models workspace scope in `flags`, not `permissions`),
   * so letting it outrank `workspace_permissions[wid]` would silently widen a workspace's RBAC.
   */
  public static computePermissionsAndPackage(params: {
    detail: AccountDetail | null
    workspaceId: unknown
    rbacPermissions?: PermissionMap | null | undefined
  }): { permissions: PermissionMap; packageId: number | null } {
    const { detail, workspaceId, rbacPermissions } = params

    const workspacePermission = detail?.workspace_permissions?.find(
      (permission) => String(permission.workspace_id) === String(workspaceId),
    )

    const permissions = (workspacePermission?.permissions ??
      rbacPermissions ??
      detail?.permissions ??
      {}) as PermissionMap
    const rawPackage = workspacePermission?.package ?? detail?.package ?? null

    return {
      permissions,
      packageId: normalizePackageId(rawPackage),
    }
  }

  /** `packageId === 12` — the single `Summary.vue` caller of the old store getter lands here (R10). */
  public static isCreativePackage(packageId: number | null): boolean {
    return packageId === CREATIVE_PACKAGE_ID
  }

  /**
   * The pricing / limit / onboarding signal (legacy `applyUserPackages`, `AccountBootstrapFlow.ts`
   * `:99-137`). The dashboards-only routing that guarded the top of the legacy method is NOT here
   * (that is the `onlyDashboard` flag + dashboards' route, ledger B9), and the two unreachable
   * legacy `else if` branches (dead after the `packageId !== 7` catch-all) are intentionally
   * dropped — only the live conditions are ported:
   *   • no package AND account not on the creative tier → pricing
   *   • packageId === 7 → pricing
   *   • workspaces / users / dashboards over their permission limit → limit (first hit wins)
   *   • zero workspaces → onboarding
   * App stays `ready` in every case.
   */
  public static computePackageSignal(input: PackageSignalInput): PackageSignal {
    const {
      packageId,
      accountPackage,
      permissions,
      workspaceCount,
      connectedUserCount,
      dashboardCount,
    } = input

    const noPackage = !packageId
    if (
      (noPackage && accountPackage !== CREATIVE_PACKAGE_ID) ||
      packageId === PRICING_LOCKED_PACKAGE_ID
    ) {
      return { kind: 'pricing' }
    }

    const workspaceLimit = toLimit(permissions[WORKSPACE_LIMIT_KEY])
    const userLimit = toLimit(permissions[USER_LIMIT_KEY])
    const dashboardLimit = toLimit(permissions[DASHBOARD_LIMIT_KEY])

    if (workspaceCount > workspaceLimit) return { kind: 'limit', reason: 'workspaces' }
    if (connectedUserCount > userLimit) return { kind: 'limit', reason: 'users' }
    if (dashboardCount > dashboardLimit) return { kind: 'limit', reason: 'dashboards' }

    if (workspaceCount === 0) return { kind: 'onboarding' }

    return { kind: 'none' }
  }
}
